import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EVA_OUTLINE_ICONS } from './eva-outline-icons';

/**
 * Seletor de ícones Eva com pré-visualização e grade pesquisável.
 *
 * Integra-se a formulários via `ngModel` ou `formControlName` (ControlValueAccessor).
 *
 * @input label - Rótulo opcional exibido acima do campo
 * @input placeholder - Placeholder do input de nome do ícone
 */
@Component({
  selector: 'ngx-icon-picker',
  templateUrl: './icon-picker.component.html',
  styleUrls: ['./icon-picker.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => IconPickerComponent),
    multi: true,
  }],
})
export class IconPickerComponent implements ControlValueAccessor, OnChanges {

  @Input() label = '';
  @Input() placeholder = 'grid-outline';

  value = '';
  pickerOpen = false;
  search = '';
  filteredIcons: string[] = EVA_OUTLINE_ICONS;
  readonly allIcons = EVA_OUTLINE_ICONS;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  /** Ícone exibido na pré-visualização (fallback se inválido). */
  get previewIcon(): string {
    return this.value && EVA_OUTLINE_ICONS.includes(this.value)
      ? this.value
      : 'question-mark-circle-outline';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.filterIcons();
    }
  }

  /**
   * Implementação ControlValueAccessor — recebe valor do formulário pai.
   * @param value - Nome do ícone Eva
   */
  writeValue(value: string): void {
    this.value = value || '';
  }

  /**
   * Registra callback de alteração do valor.
   * @param fn - Função chamada quando o ícone muda
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Registra callback de touched do controle.
   * @param fn - Função chamada ao perder foco/interagir
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Abre ou fecha o painel de seleção de ícones. */
  togglePicker(): void {
    this.pickerOpen = !this.pickerOpen;
    if (this.pickerOpen) {
      this.search = '';
      this.filterIcons();
    }
    this.onTouched();
  }

  /**
   * Atualiza valor digitado manualmente no input.
   * @param icon - Nome do ícone
   */
  onInputChange(icon: string): void {
    this.value = icon;
    this.onChange(icon);
    this.onTouched();
  }

  /**
   * Seleciona ícone na grade e fecha o painel.
   * @param icon - Nome do ícone Eva selecionado
   */
  selectIcon(icon: string): void {
    this.value = icon;
    this.onChange(icon);
    this.onTouched();
    this.pickerOpen = false;
  }

  /** Filtra ícones pela busca atual. */
  filterIcons(): void {
    const q = (this.search || '').trim().toLowerCase();
    this.filteredIcons = q
      ? EVA_OUTLINE_ICONS.filter(name => name.includes(q))
      : [...EVA_OUTLINE_ICONS];
  }
}
